import { useEffect, useMemo, useRef, useState } from "react";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Bot,
  Brain,
  Check,
  ChevronRight,
  Code2,
  Cpu,
  Download,
  GitBranch,
  Globe2,
  Laptop,
  LockKeyhole,
  LogOut,
  Megaphone,
  Menu,
  MessageSquareText,
  Mic,
  Monitor,
  Radio,
  RefreshCw,
  RotateCcw,
  Search,
  Send,
  Settings2,
  ShieldCheck,
  Speaker,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  UserRound,
  Volume2,
  Wifi,
  WifiOff,
  X,
} from "lucide-react";
import { deviceSubtitle, sortDevices } from "./lib/devices";
import {
  normalizeVoiceTranscript,
  recognitionConstructor,
  voiceStatusCopy,
} from "./lib/voice";
import {
  config,
  isAuthConfigured,
  supabase,
} from "./lib/supabase";

const FEATURES = [
  {
    icon: MessageSquareText,
    title: "Uma conversa, todos os seus contextos",
    text: "Continue no celular exatamente de onde parou no computador, com histórico organizado por máquina e projeto.",
  },
  {
    icon: Code2,
    title: "Programação no seu próprio projeto",
    text: "Envie tarefas remotamente para o computador escolhido e acompanhe execução, alterações e testes em tempo real.",
  },
  {
    icon: ShieldCheck,
    title: "Controle com autorização",
    text: "Ações sensíveis pedem confirmação. Seus arquivos permanecem no computador até você decidir compartilhar.",
  },
  {
    icon: Bot,
    title: "Memória pessoal que evolui com você",
    text: "O Jarvis aprende preferências confirmadas, recupera contexto relevante e permite revisar a memória em uma linha do tempo.",
  },
  {
    icon: MessageSquareText,
    title: "WhatsApp no mesmo centro de comando",
    text: "Leia conversas, envie mensagens e receba avisos por voz quando uma nova mensagem chegar.",
  },
  {
    icon: Sparkles,
    title: "Voz, atalhos e casa inteligente",
    text: "Chame o Jarvis por voz ou Ctrl + Alt + J e controle dispositivos compatíveis sem interromper seu trabalho.",
  },
];

const SHOWCASES = [
  {
    id: "programacao",
    label: "Programação",
    title: "Trabalhe no projeto, não apenas no chat",
    text: "Selecione uma pasta e peça ao Jarvis para ler, criar, editar e validar arquivos. A execução aparece em tempo real e o resultado volta para a mesma conversa.",
    image: "/screenshots/programacao.png",
    alt: "J.A.R.V.I.S. trabalhando em arquivos de um projeto de programação",
  },
  {
    id: "rotina",
    label: "Organização pessoal",
    title: "Converse por voz sem perder o contexto",
    text: "Use uma conversa exclusiva por voz para organizar ideias, criar lembretes e cuidar da rotina sem misturar o bate-papo com seus projetos.",
    image: "/screenshots/voz.webp",
    alt: "Modo de conversa por voz do J.A.R.V.I.S.",
  },
  {
    id: "casa",
    label: "Casa conectada",
    title: "Seus dispositivos em um painel claro",
    text: "Acompanhe e controle aparelhos compatíveis, receba estados atualizados e mantenha automações pessoais no mesmo lugar.",
    image: "/screenshots/casa-conectada.png",
    alt: "Painel de dispositivos da casa conectada no J.A.R.V.I.S.",
  },
  {
    id: "sistema",
    label: "Seu computador",
    title: "Saúde do PC sempre visível",
    text: "Discos, CPU, memória, GPU, VRAM e sensores disponíveis aparecem em uma visão única para você entender a máquina onde o Jarvis trabalha.",
    image: "/screenshots/saude-pc.png",
    alt: "Painel de saúde e desempenho do computador no J.A.R.V.I.S.",
  },
];

const CURRENT_VERSION = "1.0.56";
const DOWNLOAD_URL = `/atualizacao/releases/Jarvis-Setup-${CURRENT_VERSION}.exe`;

function navigate(to, { replace = false } = {}) {
  const method = replace ? "replaceState" : "pushState";
  window.history[method]({}, "", to);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

function Link({ to, children, ...properties }) {
  function follow(event) {
    if (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    event.preventDefault();
    navigate(to);
  }
  return <a href={to} onClick={follow} {...properties}>{children}</a>;
}

function Navigate({ to }) {
  useEffect(() => navigate(to, { replace: true }), [to]);
  return null;
}

function Brand({ compact = false }) {
  return (
    <Link className={`brand ${compact ? "brand--compact" : ""}`} to="/">
      <img src="/jarvis-icon.png" alt="" />
      <span>J.A.R.V.I.S.</span>
      {!compact && <small>{CURRENT_VERSION}</small>}
    </Link>
  );
}

function SiteHeader() {
  const [open, setOpen] = useState(false);
  return (
    <header className="site-header">
      <Brand />
      <button
        className="mobile-menu"
        type="button"
        aria-label="Abrir menu"
        onClick={() => setOpen((value) => !value)}
      >
        {open ? <X /> : <Menu />}
      </button>
      <nav className={open ? "is-open" : ""}>
        <a href="#recursos">Recursos</a>
        <a href="#por-dentro">Por dentro</a>
        <a href="#como-funciona">Como funciona</a>
        <a href="#seguranca">Segurança</a>
        <a className="nav-download" href={DOWNLOAD_URL}>
          <Download size={15} /> Baixar app
        </a>
        <Link className="button button--ghost" to="/login">
          Entrar
        </Link>
        <Link className="button button--primary" to="/login?mode=signup">
          Criar minha conta
        </Link>
      </nav>
    </header>
  );
}

function ReactorVisual() {
  return (
    <div className="reactor-stage" aria-label="Núcleo J.A.R.V.I.S. ativo">
      <div className="reactor-halo reactor-halo--outer" />
      <div className="reactor-halo reactor-halo--middle" />
      <img src="/jarvis-icon.png" alt="Símbolo oficial do J.A.R.V.I.S." />
      <div className="status-float status-float--top">
        <span className="status-dot" />
        PC Principal online
      </div>
      <div className="status-float status-float--bottom">
        <Code2 size={16} />
        Jarvis-AI-v2 conectado
      </div>
    </div>
  );
}

function LandingPage() {
  const [showcase, setShowcase] = useState(SHOWCASES[0].id);
  const activeShowcase = SHOWCASES.find((item) => item.id === showcase) ?? SHOWCASES[0];

  return (
    <div className="landing">
      <SiteHeader />
      <main>
        <section className="hero">
          <div className="hero-copy">
            <div className="eyebrow">
              <Sparkles size={15} /> Inteligência para seus projetos e para sua vida
            </div>
            <h1>
              Programe. Organize sua rotina.{" "}
              <span>Deixe o Jarvis cuidar do resto.</span>
            </h1>
            <p>
              Um assistente que trabalha nos seus arquivos, acompanha seus
              projetos, aprende suas preferências e ajuda a organizar tarefas,
              conversas, lembretes e dispositivos — no computador ou notebook.
            </p>
            <div className="hero-actions">
              <a className="button button--primary button--large" href={DOWNLOAD_URL}>
                Baixar para Windows <Download size={18} />
              </a>
              <Link className="button button--ghost button--large" to="/login?mode=signup">
                Criar minha conta <ArrowRight size={18} />
              </Link>
            </div>
            <div className="trust-row">
              <span><Check /> Programa com você</span>
              <span><Check /> Organiza sua rotina</span>
              <span><Check /> Memória pessoal</span>
            </div>
          </div>
          <ReactorVisual />
        </section>

        <section className="signal-strip" aria-label="Capacidades">
          <span><Radio /> Voz e presença</span>
          <span><Code2 /> Agente de programação</span>
          <span><Globe2 /> Acesso pelo navegador</span>
          <span><Cpu /> Automação local</span>
        </section>

        <section className="release-strip" aria-label={`Novidades da versão ${CURRENT_VERSION}`}>
          <div>
            <span className="release-badge">VERSÃO {CURRENT_VERSION}</span>
            <strong>Voz isolada, aprendizado pessoal e uma casa conectada melhor.</strong>
            <p>GPU e VRAM, perfil aprendido por chat e voz, nova Casa conectada e correções de estabilidade.</p>
          </div>
          <a className="button button--ghost" href={DOWNLOAD_URL}>
            <Download size={17} /> Baixar atualização
          </a>
        </section>

        <section className="section product-tour" id="por-dentro">
          <div className="section-heading">
            <span>CONHEÇA POR DENTRO</span>
            <h2>Uma ferramenta para construir projetos e organizar a vida.</h2>
            <p>
              O mesmo Jarvis acompanha um trabalho de programação, uma conversa
              por voz e as rotinas do seu computador sem perder o contexto.
            </p>
          </div>
          <div className="showcase-tabs" role="tablist" aria-label="Telas do J.A.R.V.I.S.">
            {SHOWCASES.map((item) => (
              <button
                className={item.id === activeShowcase.id ? "is-active" : ""}
                key={item.id}
                type="button"
                role="tab"
                aria-selected={item.id === activeShowcase.id}
                onClick={() => setShowcase(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>
          <div className="showcase-panel">
            <div className="showcase-copy">
              <small>{activeShowcase.label}</small>
              <h3>{activeShowcase.title}</h3>
              <p>{activeShowcase.text}</p>
              <a href={DOWNLOAD_URL}>Experimentar no Windows <ArrowRight size={17} /></a>
            </div>
            <div className="showcase-window">
              <div className="showcase-window__bar">
                <span /><span /><span />
                <b>J.A.R.V.I.S.</b>
              </div>
              <img src={activeShowcase.image} alt={activeShowcase.alt} />
            </div>
          </div>
        </section>

        <section className="section" id="recursos">
          <div className="section-heading">
            <span>UM JARVIS DE VERDADE</span>
            <h2>Não é só um chat. É a ponte com o seu computador.</h2>
          </div>
          <div className="feature-grid">
            {FEATURES.map(({ icon: Icon, title, text }) => (
              <article className="feature-card" key={title}>
                <div className="feature-icon"><Icon /></div>
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="section workflow" id="como-funciona">
          <div className="workflow-panel">
            <div className="section-heading section-heading--left">
              <span>SIMPLES PARA USAR</span>
              <h2>Uma conta. Seus computadores. Seus projetos.</h2>
              <p>
                Instale o Jarvis no PC, conecte sua conta e ele aparecerá
                automaticamente no painel do celular.
              </p>
            </div>
            <ol className="steps">
              <li><b>01</b><div><strong>Conecte o computador</strong><span>O aplicativo permanece protegido e ativo em segundo plano.</span></div></li>
              <li><b>02</b><div><strong>Escolha pelo celular</strong><span>Veja quais máquinas estão online e qual projeto está aberto.</span></div></li>
              <li><b>03</b><div><strong>Converse ou envie uma tarefa</strong><span>Acompanhe as etapas e receba o resultado no mesmo histórico.</span></div></li>
            </ol>
          </div>
          <div className="device-preview">
            <div className="preview-top">
              <span>MEUS COMPUTADORES</span>
              <small>2 dispositivos</small>
            </div>
            <div className="preview-device preview-device--selected">
              <div className="device-icon"><Monitor /></div>
              <div><strong>PC Principal</strong><span>Online · Jarvis-AI-v2</span></div>
              <Wifi />
            </div>
            <div className="preview-device">
              <div className="device-icon"><Laptop /></div>
              <div><strong>Notebook</strong><span>Offline · visto ontem</span></div>
              <WifiOff />
            </div>
            <div className="preview-command">
              <span>Peça algo ao Jarvis…</span><button aria-label="Enviar"><Send /></button>
            </div>
          </div>
        </section>

        <section className="section security" id="seguranca">
          <LockKeyhole />
          <div>
            <span>SEGURANÇA DESDE A BASE</span>
            <h2>Seu computador não fica exposto na internet.</h2>
            <p>
              A conexão parte do aplicativo para o servidor, sempre protegida.
              Dados são separados por conta e ações importantes exigem sua
              autorização.
            </p>
          </div>
        </section>

        <section className="final-cta">
          <img src="/jarvis-icon.png" alt="" />
          <div>
            <span>J.A.R.V.I.S. {CURRENT_VERSION}</span>
            <h2>Seus projetos e sua vida em um só assistente.</h2>
          </div>
          <a className="button button--primary button--large" href={DOWNLOAD_URL}>
            Baixar para Windows <Download />
          </a>
        </section>
      </main>
      <footer>
        <Brand compact />
        <p>© 2026 Filipe Oliveira. Todos os direitos reservados.</p>
        <span>Privacidade · Termos · Suporte</span>
      </footer>
    </div>
  );
}

function LoginPage({ session }) {
  const initialSignup = new URLSearchParams(window.location.search).get("mode") === "signup";
  const [signup, setSignup] = useState(initialSignup);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  if (session) return <Navigate to="/app" replace />;

  async function submit(event) {
    event.preventDefault();
    if (!supabase) {
      setMessage("O login ainda aguarda a configuração segura do servidor.");
      return;
    }
    setBusy(true);
    setMessage("");
    const result = signup
      ? await supabase.auth.signUp({
          email,
          password,
          options: { data: { display_name: name } },
        })
      : await supabase.auth.signInWithPassword({ email, password });
    setBusy(false);
    if (result.error) {
      setMessage(result.error.message);
      return;
    }
    if (signup && !result.data.session) {
      setMessage("Conta criada. Confirme seu e-mail para entrar.");
      return;
    }
    navigate("/app");
  }

  return (
    <div className="auth-page">
      <div className="auth-visual">
        <Brand />
        <div className="auth-promise">
          <div className="eyebrow"><Radio size={15} /> Conexão segura ativa</div>
          <h1>Seu Jarvis.<br />Em qualquer tela.</h1>
          <p>Entre para acessar computadores, conversas e projetos vinculados à sua conta.</p>
        </div>
        <ReactorVisual />
        <small>© 2026 Filipe Oliveira</small>
      </div>
      <main className="auth-card-wrap">
        <Link className="auth-back" to="/">← Voltar para o site</Link>
        <form className="auth-card" onSubmit={submit}>
          <img src="/jarvis-icon.png" alt="" />
          <span className="auth-kicker">{signup ? "NOVA CONTA" : "ACESSO SEGURO"}</span>
          <h2>{signup ? "Comece com o Jarvis" : "Bem-vindo de volta"}</h2>
          <p>{signup ? "Crie sua identidade para conectar o primeiro computador." : "Use a mesma conta cadastrada no aplicativo."}</p>
          {signup && (
            <label>Seu nome
              <input autoComplete="name" value={name} onChange={(event) => setName(event.target.value)} required />
            </label>
          )}
          <label>E-mail
            <input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
          </label>
          <label>Senha
            <input type="password" minLength="8" autoComplete={signup ? "new-password" : "current-password"} value={password} onChange={(event) => setPassword(event.target.value)} required />
          </label>
          {message && <div className="auth-message">{message}</div>}
          {!isAuthConfigured && <div className="auth-message">Ambiente aguardando conexão com o Supabase.</div>}
          <button className="button button--primary button--large" disabled={busy} type="submit">
            {busy ? "Aguarde…" : signup ? "Criar conta" : "Entrar"} <ArrowRight />
          </button>
          <button className="auth-switch" type="button" onClick={() => { setSignup((value) => !value); setMessage(""); }}>
            {signup ? "Já tenho uma conta" : "Ainda não tenho uma conta"}
          </button>
        </form>
      </main>
    </div>
  );
}

function AppShell({ session }) {
  const [section, setSection] = useState("computers");
  const [devices, setDevices] = useState([]);
  const [selectedId, setSelectedId] = useState(
    () => window.localStorage.getItem("jarvis-selected-device") || "",
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [request, setRequest] = useState("");
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState("");
  const [history, setHistory] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [chatDeviceId, setChatDeviceId] = useState("");
  const [conversationId, setConversationId] = useState("");
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatBusy, setChatBusy] = useState(false);
  const [chatNotice, setChatNotice] = useState("");
  const [remoteTask, setRemoteTask] = useState(null);
  const [listening, setListening] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [voiceStatus, setVoiceStatus] = useState("waiting");
  const [voiceTranscript, setVoiceTranscript] = useState("");
  const recognitionRef = useRef(null);
  const voiceModeRef = useRef(false);
  const spokenRemoteTaskRef = useRef("");
  const [providers, setProviders] = useState([]);
  const [providerId, setProviderId] = useState("");
  const [providerForm, setProviderForm] = useState({
    endpoint: "",
    model: "",
    api_key: "",
    enabled: false,
  });
  const [providerNotice, setProviderNotice] = useState("");
  const [alexaTarget, setAlexaTarget] = useState("");
  const [alexaMessage, setAlexaMessage] = useState("");
  const [alexaVolume, setAlexaVolume] = useState(30);
  const [alexaCommand, setAlexaCommand] = useState("");
  const [alexaSending, setAlexaSending] = useState(false);
  const [alexaNotice, setAlexaNotice] = useState("");

  const selected = useMemo(
    () => devices.find((device) => device.id === selectedId) ?? devices[0],
    [devices, selectedId],
  );

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!session) return;
      try {
        const response = await fetch(`${config.apiBaseUrl}/v1/devices`, {
          headers: { Authorization: `Bearer ${session.access_token}` },
        });
        if (!response.ok) throw new Error("Não foi possível carregar seus computadores.");
        const records = sortDevices(await response.json());
        if (!cancelled) {
          setDevices(records);
          setSelectedId((current) => (
            records.some((item) => item.id === current)
              ? current
              : records[0]?.id || ""
          ));
        }
      } catch (loadError) {
        if (!cancelled) setError(loadError.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    const timer = setInterval(load, 15000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [session]);

  useEffect(() => {
    if (!session || !selected?.id) {
      setHistory([]);
      return;
    }
    let cancelled = false;
    async function loadHistory() {
      try {
        const response = await fetch(
          `${config.apiBaseUrl}/v1/tasks?device_id=${encodeURIComponent(selected.id)}`,
          { headers: { Authorization: `Bearer ${session.access_token}` } },
        );
        if (!response.ok) return;
        const records = await response.json();
        if (!cancelled) setHistory(records.reverse());
      } catch {
        // A lista de computadores continua utilizável durante uma oscilação.
      }
    }
    loadHistory();
    const timer = setInterval(loadHistory, 3000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [selected?.id, session]);

  useEffect(() => {
    if (!session || section !== "chat") return;
    loadConversations(chatDeviceId);
    const timer = setInterval(() => loadConversations(chatDeviceId), 5000);
    return () => clearInterval(timer);
  }, [section, session, chatDeviceId]);

  useEffect(() => {
    if (!session || section !== "settings") return;
    loadProviders();
  }, [section, session]);

  useEffect(() => {
    if (!session) return undefined;
    if (!conversationId) {
      setMessages([]);
    } else {
      loadMessages(conversationId);
    }
    if (!chatDeviceId) return undefined;
    loadDeviceActivity(chatDeviceId, conversationId);
    const timer = setInterval(() => {
      if (conversationId) loadMessages(conversationId);
      loadDeviceActivity(chatDeviceId, conversationId);
    }, 2500);
    return () => clearInterval(timer);
  }, [conversationId, session, chatDeviceId]);

  useEffect(() => {
    if (
      !voiceOpen
      || !chatDeviceId
      || !remoteTask?.result
      || remoteTask.status !== "completed"
      || spokenRemoteTaskRef.current === remoteTask.id
    ) {
      return;
    }
    spokenRemoteTaskRef.current = remoteTask.id;
    speakVoiceResponse(remoteTask.result);
  }, [voiceOpen, chatDeviceId, remoteTask]);

  useEffect(() => () => {
    voiceModeRef.current = false;
    recognitionRef.current?.abort();
    window.speechSynthesis?.cancel();
  }, []);

  const accessToken = session?.access_token || "";

  async function signOut() {
    await supabase?.auth.signOut();
    navigate("/login");
  }

  function selectDevice(deviceId) {
    setSelectedId(deviceId);
    window.localStorage.setItem("jarvis-selected-device", deviceId);
  }

  function openDeviceConversations() {
    if (!selected?.id) return;
    selectChatSource(selected.id);
    setSection("chat");
  }

  async function sendTask(event) {
    event.preventDefault();
    if (!selected || !request.trim()) return;
    setSending(true);
    setNotice("");
    try {
      const response = await fetch(`${config.apiBaseUrl}/v1/tasks`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          device_id: selected.id,
          request: request.trim(),
          risk: "safe",
        }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.detail || "Tarefa não enviada.");
      setRequest("");
      setHistory((current) => [...current, result]);
      setNotice(result.status === "dispatched" ? "Tarefa entregue ao computador." : "Computador offline. Tarefa guardada na fila.");
    } catch (sendError) {
      setNotice(sendError.message);
    } finally {
      setSending(false);
    }
  }

  async function dispatchAlexa(command) {
    if (!selected || !command.trim()) return;
    setAlexaSending(true);
    setAlexaNotice("");
    try {
      const response = await fetch(`${config.apiBaseUrl}/v1/tasks`, {
        method: "POST",
        headers: authHeaders(true),
        body: JSON.stringify({
          device_id: selected.id,
          request: command.trim(),
          risk: "safe",
        }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.detail || "Comando Alexa não enviado.");
      setHistory((current) => [...current, result]);
      setAlexaNotice(
        result.status === "dispatched"
          ? "Comando entregue ao computador."
          : "Computador offline. O comando ficou aguardando na fila.",
      );
    } catch (sendError) {
      setAlexaNotice(sendError.message);
    } finally {
      setAlexaSending(false);
    }
  }

  function authHeaders(json = false) {
    return {
      Authorization: `Bearer ${session.access_token}`,
      ...(json ? { "Content-Type": "application/json" } : {}),
    };
  }

  async function loadConversations(deviceId = chatDeviceId) {
    try {
      const query = deviceId
        ? `?device_id=${encodeURIComponent(deviceId)}`
        : "";
      const response = await fetch(`${config.apiBaseUrl}/v1/conversations${query}`, {
        headers: authHeaders(),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.detail || "Não foi possível carregar as conversas.");
      setConversations(result);
      setConversationId((current) => (
        current && result.some((item) => item.id === current)
          ? current
          : result[0]?.id || ""
      ));
    } catch (loadError) {
      setChatNotice(loadError.message);
    }
  }

  async function loadMessages(id) {
    try {
      const response = await fetch(
        `${config.apiBaseUrl}/v1/conversations/${encodeURIComponent(id)}/messages`,
        { headers: authHeaders() },
      );
      const result = await response.json();
      if (!response.ok) throw new Error(result.detail || "Não foi possível carregar as mensagens.");
      setMessages(result);
    } catch (loadError) {
      setChatNotice(loadError.message);
    }
  }

  async function loadDeviceActivity(deviceId, selectedConversationId) {
    try {
      const response = await fetch(
        `${config.apiBaseUrl}/v1/tasks?device_id=${encodeURIComponent(deviceId)}`,
        { headers: authHeaders() },
      );
      if (!response.ok) return;
      const tasks = await response.json();
      const matching = tasks.find((task) => (
        selectedConversationId
          ? task.conversation_id === selectedConversationId
          : !task.conversation_id
      ));
      setRemoteTask(matching || null);
    } catch {
      // O histórico da conversa continua disponível durante uma oscilação.
    }
  }

  async function submitChatMessage(rawText, fromVoice = false) {
    const text = normalizeVoiceTranscript(rawText);
    if (!text || chatBusy) return;
    setChatBusy(true);
    setChatNotice("");
    if (fromVoice) {
      setVoiceStatus("thinking");
      setVoiceTranscript(text);
    }
    setMessages((current) => [
      ...current,
      { id: `local-${Date.now()}`, role: "user", content: text },
    ]);
    setChatInput("");
    try {
      if (chatDeviceId) {
        const response = await fetch(`${config.apiBaseUrl}/v1/tasks`, {
          method: "POST",
          headers: authHeaders(true),
          body: JSON.stringify({
            device_id: chatDeviceId,
            conversation_id: conversationId || null,
            request: text,
            risk: "safe",
          }),
        });
        const result = await response.json();
        if (!response.ok) throw new Error(result.detail || "A tarefa não foi enviada ao computador.");
        setChatNotice(
          result.status === "dispatched"
            ? "Mensagem entregue ao computador. A resposta aparecerá aqui."
            : "Computador offline. A mensagem ficou aguardando na fila.",
        );
        setRemoteTask(result);
        return;
      }
      const response = await fetch(`${config.apiBaseUrl}/v1/chat`, {
        method: "POST",
        headers: authHeaders(true),
        body: JSON.stringify({
          message: text,
          conversation_id: conversationId || null,
        }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.detail || "O Jarvis não conseguiu responder.");
      setConversationId(result.conversation_id);
      setMessages((current) => [...current, result.message]);
      await loadConversations("");
      if (fromVoice) {
        speakVoiceResponse(result.message.content);
      } else if ("speechSynthesis" in window) {
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(result.message.content);
        utterance.lang = "pt-BR";
        window.speechSynthesis.speak(utterance);
      }
    } catch (sendError) {
      setChatNotice(sendError.message);
      if (fromVoice) {
        setVoiceStatus("error");
        setVoiceTranscript(sendError.message);
      }
    } finally {
      setChatBusy(false);
    }
  }

  function sendChat(event) {
    event.preventDefault();
    void submitChatMessage(chatInput);
  }

  function selectChatSource(deviceId) {
    setChatDeviceId(deviceId);
    setConversationId("");
    setMessages([]);
    setRemoteTask(null);
    setChatNotice("");
  }

  function startVoiceCapture() {
    if (recognitionRef.current || !voiceModeRef.current) return;
    const Recognition = recognitionConstructor(window);
    if (!Recognition) {
      const message = "Este navegador não oferece reconhecimento de voz. Use Chrome ou Edge atualizado.";
      setChatNotice(message);
      setVoiceStatus("error");
      setVoiceTranscript(message);
      return;
    }
    const recognition = new Recognition();
    recognitionRef.current = recognition;
    recognition.lang = "pt-BR";
    recognition.interimResults = true;
    recognition.continuous = false;
    let finalTranscript = "";
    recognition.onstart = () => {
      setListening(true);
      setVoiceStatus("listening");
      setVoiceTranscript("");
    };
    recognition.onend = () => {
      recognitionRef.current = null;
      setListening(false);
      if (voiceModeRef.current && !finalTranscript) setVoiceStatus("waiting");
    };
    recognition.onerror = (event) => {
      recognitionRef.current = null;
      setListening(false);
      const message = event.error === "not-allowed"
        ? "Permita o uso do microfone para conversar com o Jarvis."
        : "Não consegui ouvir. Toque no núcleo para tentar novamente.";
      setChatNotice(message);
      setVoiceStatus("error");
      setVoiceTranscript(message);
    };
    recognition.onresult = (event) => {
      let interimTranscript = "";
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const phrase = event.results[index][0].transcript;
        if (event.results[index].isFinal) finalTranscript += phrase;
        else interimTranscript += phrase;
      }
      const visible = normalizeVoiceTranscript(finalTranscript || interimTranscript);
      setVoiceTranscript(visible);
      if (finalTranscript) {
        const requestText = normalizeVoiceTranscript(finalTranscript);
        setChatInput("");
        void submitChatMessage(requestText, true);
      }
    };
    try {
      recognition.start();
    } catch {
      recognitionRef.current = null;
      setVoiceStatus("error");
      setVoiceTranscript("O microfone já está em uso. Tente novamente.");
    }
  }

  function openVoiceMode() {
    voiceModeRef.current = true;
    setVoiceOpen(true);
    setSection("chat");
    setVoiceStatus("preparing");
    setVoiceTranscript("");
    window.setTimeout(startVoiceCapture, 280);
  }

  function closeVoiceMode() {
    voiceModeRef.current = false;
    recognitionRef.current?.abort();
    recognitionRef.current = null;
    window.speechSynthesis?.cancel();
    setListening(false);
    setVoiceOpen(false);
    setVoiceStatus("waiting");
  }

  function speakVoiceResponse(text) {
    const answer = normalizeVoiceTranscript(text);
    setVoiceTranscript(answer);
    if (!voiceModeRef.current) return;
    if (!("speechSynthesis" in window)) {
      setVoiceStatus("waiting");
      return;
    }
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(answer);
    utterance.lang = "pt-BR";
    utterance.onstart = () => setVoiceStatus("speaking");
    utterance.onend = () => {
      if (!voiceModeRef.current) return;
      setVoiceStatus("waiting");
      window.setTimeout(startVoiceCapture, 350);
    };
    utterance.onerror = () => {
      if (voiceModeRef.current) setVoiceStatus("waiting");
    };
    window.speechSynthesis.speak(utterance);
  }

  async function loadProviders() {
    try {
      const response = await fetch(`${config.apiBaseUrl}/v1/providers`, {
        headers: authHeaders(),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.detail || "Não foi possível carregar as IAs.");
      setProviders(result);
      const active = result.find((item) => item.enabled) || result[0];
      if (active) selectProvider(active, result);
    } catch (loadError) {
      setProviderNotice(loadError.message);
    }
  }

  function selectProvider(provider, source = providers) {
    const selectedProvider = typeof provider === "string"
      ? source.find((item) => item.id === provider)
      : provider;
    if (!selectedProvider) return;
    setProviderId(selectedProvider.id);
    setProviderForm({
      endpoint: selectedProvider.endpoint || "",
      model: selectedProvider.model || "",
      api_key: "",
      enabled: Boolean(selectedProvider.enabled),
    });
    setProviderNotice("");
  }

  async function saveProvider(event) {
    event.preventDefault();
    if (!providerId) return;
    setProviderNotice("Salvando…");
    try {
      const response = await fetch(
        `${config.apiBaseUrl}/v1/providers/${encodeURIComponent(providerId)}`,
        {
          method: "PUT",
          headers: authHeaders(true),
          body: JSON.stringify({ provider: providerId, ...providerForm }),
        },
      );
      const result = await response.json();
      if (!response.ok) throw new Error(result.detail || "Não foi possível salvar a IA.");
      setProviderNotice("IA salva com segurança no servidor.");
      await loadProviders();
    } catch (saveError) {
      setProviderNotice(saveError.message);
    }
  }

  return (
    <div className="app-shell">
      <aside className="app-sidebar">
        <Brand compact />
        <nav>
          <button className={section === "computers" ? "active" : ""} onClick={() => setSection("computers")}><Monitor /> Computadores</button>
          <button className={section === "chat" ? "active" : ""} onClick={() => setSection("chat")}><MessageSquareText /> Conversas</button>
          <button className={`nav-voice ${voiceOpen ? "active" : ""}`} onClick={openVoiceMode}><Mic /> Falar</button>
          <button className={section === "alexa" ? "active" : ""} onClick={() => setSection("alexa")}><Speaker /> Alexa</button>
          <Link className="app-nav-link" to="/memoria"><Brain /> Memória</Link>
          <button className={section === "settings" ? "active" : ""} onClick={() => setSection("settings")}><Settings2 /> Configurar IA</button>
        </nav>
        <div className="account-mini">
          <span>{session.user.email?.slice(0, 1).toUpperCase()}</span>
          <div><strong>{session.user.user_metadata?.display_name || "Minha conta"}</strong><small>{session.user.email}</small></div>
          <button onClick={signOut} aria-label="Sair"><LogOut /></button>
        </div>
      </aside>
      {section === "computers" && <main className="dashboard">
        <header className="dashboard-header">
          <div><span>PAINEL REMOTO</span><h1>Seus computadores</h1></div>
          <div className="secure-pill"><LockKeyhole /> Conexão protegida</div>
        </header>
        {error && <div className="dashboard-alert">{error}</div>}
        <section className="device-section">
          <div className="section-label"><span>ESCOLHA ONDE O JARVIS DEVE TRABALHAR</span><small>{devices.filter((item) => item.status === "online").length} online</small></div>
          <div className="device-grid">
            {loading && <div className="empty-card">Procurando seus computadores…</div>}
            {!loading && !devices.length && (
              <div className="empty-card">
                <Download />
                <h3>Conecte seu primeiro computador</h3>
                <p>Entre com esta conta no aplicativo J.A.R.V.I.S. para ele aparecer aqui.</p>
              </div>
            )}
            {devices.map((device) => (
              <button
                className={`device-card ${selected?.id === device.id ? "selected" : ""}`}
                key={device.id}
                onClick={() => selectDevice(device.id)}
              >
                <div className="device-card-icon"><Monitor /></div>
                <div><strong>{device.name}</strong><span>{deviceSubtitle(device)}</span></div>
                <div className={`online-indicator ${device.status}`}><i />{device.status === "online" ? "ONLINE" : "OFFLINE"}</div>
              </button>
            ))}
          </div>
        </section>
        <section className="command-center">
          <div className="command-heading">
            <div className="mini-reactor"><img src="/jarvis-icon.png" alt="" /></div>
            <div>
              <span>J.A.R.V.I.S. · {selected?.name || "Nenhum computador"}</span>
              <h2>{selected?.status === "online" ? "Pronto para trabalhar" : "Aguardando computador"}</h2>
            </div>
            {selected && (
              <button className="device-conversations-button" onClick={openDeviceConversations}>
                <MessageSquareText /> Abrir conversas
              </button>
            )}
          </div>
          <div className={`conversation-placeholder ${history.length ? "has-history" : ""}`}>
            {!history.length ? (
              <>
                <Bot />
                <h3>O que vamos fazer agora?</h3>
                <p>Peça uma informação ou envie uma tarefa para o projeto aberto no computador selecionado.</p>
              </>
            ) : (
              <div className="remote-history">
                {history.map((task) => (
                  <div className="remote-exchange" key={task.id}>
                    <div className="remote-message remote-message--user">{task.request}</div>
                    <div className={`remote-message remote-message--assistant ${task.status}`}>
                      {task.result || (
                        task.status === "queued"
                          ? "Aguardando o computador ficar online…"
                          : "Jarvis está trabalhando…"
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {notice && <div className="task-notice">{notice}</div>}
          <form className="remote-composer" onSubmit={sendTask}>
            <textarea
              placeholder={selected ? `Enviar tarefa para ${selected.name}…` : "Selecione um computador…"}
              value={request}
              onChange={(event) => setRequest(event.target.value)}
              disabled={!selected}
              rows="2"
            />
            <button className="button button--primary" type="submit" disabled={!selected || sending}>
              <Send /> {sending ? "Enviando…" : "Enviar"}
            </button>
          </form>
        </section>
      </main>}
      {section === "chat" && (
        <main className="dashboard">
          <header className="dashboard-header">
            <div><span>JARVIS NO SERVIDOR</span><h1>Conversas</h1></div>
            <div className="secure-pill">
              {chatDeviceId ? <Monitor /> : <Wifi />}
              {chatDeviceId ? "Conectada ao computador" : "Respondida pelo servidor"}
            </div>
          </header>
          <div className="chat-source-picker">
            <span>CONVERSAR COM</span>
            <button
              className={!chatDeviceId ? "selected" : ""}
              onClick={() => selectChatSource("")}
            >
              <Bot /> Servidor
            </button>
            {devices.map((device) => (
              <button
                key={device.id}
                className={chatDeviceId === device.id ? "selected" : ""}
                onClick={() => selectChatSource(device.id)}
              >
                <Monitor /> {device.name}
                <i className={device.status} />
              </button>
            ))}
          </div>
          <div className="server-chat-layout">
            <aside className="conversation-list">
              <div className="section-label"><span>HISTÓRICO</span></div>
              <button
                className={!conversationId ? "selected" : ""}
                onClick={() => { setConversationId(""); setMessages([]); }}
              >
                + {chatDeviceId ? "Nova no computador" : "Nova conversa"}
              </button>
              {conversations.map((conversation) => (
                <button
                  key={conversation.id}
                  className={conversationId === conversation.id ? "selected" : ""}
                  onClick={() => setConversationId(conversation.id)}
                >
                  <strong>{conversation.title}</strong>
                  {conversation.desktop_workspace_name && (
                    <small>
                      {devices.find((item) => item.id === conversation.device_id)?.name || "Computador"}
                      {" · "}
                      {conversation.desktop_workspace_name}
                    </small>
                  )}
                </button>
              ))}
            </aside>
            <section className="command-center server-chat">
              <div className="command-heading">
                <div className="mini-reactor"><img src="/jarvis-icon.png" alt="" /></div>
                <div>
                  <span>{chatDeviceId ? "CONVERSA DO COMPUTADOR" : "CONVERSA COMUM"}</span>
                  <h2>
                    {chatDeviceId
                      ? devices.find((item) => item.id === chatDeviceId)?.name || "Computador"
                      : "J.A.R.V.I.S. online"}
                  </h2>
                </div>
              </div>
              <div className={`conversation-placeholder ${messages.length ? "has-history" : ""}`}>
                {!messages.length ? (
                  <>
                    <Bot />
                    <h3>
                      {chatDeviceId
                        ? "Continue uma conversa deste computador."
                        : "Pode falar comigo de qualquer lugar."}
                    </h3>
                    <p>
                      {chatDeviceId
                        ? "As mensagens, o projeto e a execução são sincronizados com o aplicativo selecionado."
                        : "Esta conversa é respondida pelo servidor, mesmo com o aplicativo fechado."}
                    </p>
                  </>
                ) : (
                  <div className="remote-history">
                    {messages.map((message) => (
                      <div
                        key={message.id}
                        className={`remote-message remote-message--${message.role === "user" ? "user" : "assistant"}`}
                      >
                        {message.content}
                      </div>
                    ))}
                    {chatBusy && <div className="remote-message remote-message--assistant">Pensando…</div>}
                  </div>
                )}
              </div>
              {chatNotice && <div className="task-notice">{chatNotice}</div>}
              {chatDeviceId && remoteTask && (
                <div className={`remote-progress ${remoteTask.status}`}>
                  <Activity />
                  <div>
                    <strong>
                      {remoteTask.status === "completed"
                        ? "Concluído"
                        : remoteTask.status === "failed"
                          ? "Falhou"
                          : remoteTask.status === "queued"
                            ? "Aguardando computador"
                            : "Jarvis trabalhando no computador"}
                    </strong>
                    <span>{remoteTask.result || remoteTask.request}</span>
                  </div>
                </div>
              )}
              <form className="remote-composer" onSubmit={sendChat}>
                <textarea
                  placeholder="Converse com o Jarvis…"
                  value={chatInput}
                  onChange={(event) => setChatInput(event.target.value)}
                  rows="2"
                />
                <button
                  className={`voice-button ${listening ? "listening" : ""}`}
                  type="button"
                  onClick={openVoiceMode}
                  aria-label="Falar com o Jarvis"
                >
                  <Mic /> Falar
                </button>
                <button className="button button--primary" type="submit" disabled={chatBusy || !chatInput.trim()}>
                  <Send /> {chatBusy ? "Enviando…" : "Enviar"}
                </button>
              </form>
            </section>
          </div>
        </main>
      )}
      {section === "alexa" && (
        <main className="dashboard">
          <header className="dashboard-header">
            <div><span>CASA CONECTADA</span><h1>Controle remoto Alexa</h1></div>
            <div className="secure-pill"><LockKeyhole /> Executado pelo seu computador</div>
          </header>
          <div className="chat-source-picker">
            <span>COMPUTADOR</span>
            {devices.map((device) => (
              <button
                key={device.id}
                className={selected?.id === device.id ? "selected" : ""}
                onClick={() => selectDevice(device.id)}
              >
                <Monitor /> {device.name}
                <i className={device.status} />
              </button>
            ))}
          </div>
          <section className="alexa-remote-panel">
            <div className="alexa-remote-heading">
              <div className="mini-reactor"><img src="/jarvis-icon.png" alt="" /></div>
              <div>
                <span>ALEXA · {selected?.name || "Selecione um computador"}</span>
                <h2>{selected?.status === "online" ? "Pronta para comandos" : "Comandos serão enfileirados"}</h2>
              </div>
              <button
                className="device-conversations-button"
                disabled={!selected || alexaSending}
                onClick={() => dispatchAlexa("Liste os dispositivos Alexa")}
              >
                <Speaker /> Atualizar aparelhos
              </button>
            </div>
            <div className="alexa-control-grid">
              <label>
                Nome do aparelho
                <input
                  value={alexaTarget}
                  onChange={(event) => setAlexaTarget(event.target.value)}
                  placeholder="Ex.: Sala ou Echo Show"
                />
              </label>
              <label>
                Anúncio
                <div className="alexa-inline-action">
                  <input
                    value={alexaMessage}
                    onChange={(event) => setAlexaMessage(event.target.value)}
                    placeholder="Mensagem que a Alexa deve falar"
                  />
                  <button
                    disabled={!selected || !alexaTarget.trim() || !alexaMessage.trim() || alexaSending}
                    onClick={() => dispatchAlexa(`Alexa anuncie ${alexaMessage} na Alexa ${alexaTarget}`)}
                  >
                    <Megaphone /> Anunciar
                  </button>
                </div>
              </label>
              <label>
                Volume · {alexaVolume}%
                <div className="alexa-inline-action">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={alexaVolume}
                    onChange={(event) => setAlexaVolume(Number(event.target.value))}
                  />
                  <button
                    disabled={!selected || !alexaTarget.trim() || alexaSending}
                    onClick={() => dispatchAlexa(`Volume ${alexaVolume}% na Alexa ${alexaTarget}`)}
                  >
                    <Volume2 /> Aplicar
                  </button>
                </div>
              </label>
              <label>
                Comando livre
                <div className="alexa-inline-action">
                  <input
                    value={alexaCommand}
                    onChange={(event) => setAlexaCommand(event.target.value)}
                    placeholder="Ex.: peça para Alexa sala para acender a luz"
                  />
                  <button
                    disabled={!selected || !alexaCommand.trim() || alexaSending}
                    onClick={() => { dispatchAlexa(alexaCommand); setAlexaCommand(""); }}
                  >
                    <Send /> Enviar
                  </button>
                </div>
              </label>
            </div>
            {alexaNotice && <div className="task-notice">{alexaNotice}</div>}
            <div className="alexa-recent">
              <span>ATIVIDADE RECENTE</span>
              {history.slice(-5).reverse().map((task) => (
                <div key={task.id}>
                  <strong>{task.request}</strong>
                  <small>{task.result || (task.status === "queued" ? "Aguardando computador…" : "Executando…")}</small>
                </div>
              ))}
            </div>
          </section>
        </main>
      )}
      {section === "settings" && (
        <main className="dashboard">
          <header className="dashboard-header">
            <div><span>CONFIGURAÇÃO REMOTA</span><h1>Inteligência artificial</h1></div>
            <div className="secure-pill"><LockKeyhole /> Chaves criptografadas</div>
          </header>
          <section className="provider-settings">
            <div className="provider-picker">
              <div className="section-label"><span>PROVEDORES</span></div>
              {providers.map((provider) => (
                <button
                  key={provider.id}
                  className={providerId === provider.id ? "selected" : ""}
                  onClick={() => selectProvider(provider)}
                >
                  <strong>{provider.name}</strong>
                  <span>{provider.enabled ? "ATIVA" : provider.has_api_key ? "CONFIGURADA" : "NÃO CONFIGURADA"}</span>
                </button>
              ))}
            </div>
            <form className="provider-form" onSubmit={saveProvider}>
              <div>
                <span>IA DO SERVIDOR</span>
                <h2>{providers.find((item) => item.id === providerId)?.name || "Selecione um provedor"}</h2>
                <p>Esta IA responde ao chat e à voz mesmo quando o aplicativo do computador está fechado.</p>
              </div>
              <label>Endpoint
                <input
                  type="url"
                  value={providerForm.endpoint}
                  onChange={(event) => setProviderForm((current) => ({ ...current, endpoint: event.target.value }))}
                  required
                />
              </label>
              <label>Modelo
                <input
                  value={providerForm.model}
                  onChange={(event) => setProviderForm((current) => ({ ...current, model: event.target.value }))}
                  placeholder="Ex.: gpt-4.1-mini ou modelo disponível no provedor"
                  required={providerForm.enabled}
                />
              </label>
              <label>Chave da API
                <input
                  type="password"
                  autoComplete="new-password"
                  value={providerForm.api_key}
                  onChange={(event) => setProviderForm((current) => ({ ...current, api_key: event.target.value }))}
                  placeholder={providers.find((item) => item.id === providerId)?.has_api_key ? "Chave já protegida — deixe vazio para manter" : "Cole a chave aqui"}
                />
              </label>
              <label className="provider-toggle">
                <input
                  type="checkbox"
                  checked={providerForm.enabled}
                  onChange={(event) => setProviderForm((current) => ({ ...current, enabled: event.target.checked }))}
                />
                Usar esta IA no chat remoto
              </label>
              {providerNotice && <div className="task-notice">{providerNotice}</div>}
              <button className="button button--primary" type="submit" disabled={!providerId}>
                <ShieldCheck /> Salvar com segurança
              </button>
            </form>
          </section>
        </main>
      )}
      {voiceOpen && (
        <div className={`voice-mode-overlay voice-mode--${voiceStatus}`} role="dialog" aria-modal="true" aria-label="Conversa por voz">
          <header className="voice-mode-header">
            <div>
              <img src="/jarvis-icon.png" alt="" />
              <span>CONVERSA POR VOZ</span>
            </div>
            <select
              value={chatDeviceId}
              onChange={(event) => {
                recognitionRef.current?.abort();
                recognitionRef.current = null;
                setListening(false);
                setVoiceStatus("waiting");
                selectChatSource(event.target.value);
              }}
              aria-label="Onde o Jarvis deve responder"
            >
              <option value="">Jarvis no servidor</option>
              {devices.map((device) => (
                <option key={device.id} value={device.id}>
                  {device.name} · {device.status === "online" ? "online" : "offline"}
                </option>
              ))}
            </select>
            <button type="button" onClick={closeVoiceMode} aria-label="Encerrar conversa por voz"><X /></button>
          </header>
          <main className="voice-mode-stage">
            <span className="voice-mode-eyebrow">NÚCLEO J.A.R.V.I.S.</span>
            <button
              className="voice-reactor"
              type="button"
              onClick={startVoiceCapture}
              disabled={voiceStatus === "thinking" || voiceStatus === "speaking"}
              aria-label="Tocar para falar"
            >
              <span className="voice-wave voice-wave--one" />
              <span className="voice-wave voice-wave--two" />
              <span className="voice-wave voice-wave--three" />
              <img src="/jarvis-icon.png" alt="" />
              <span className="voice-spectrum" aria-hidden="true">
                {Array.from({ length: 18 }, (_, index) => <i key={index} />)}
              </span>
            </button>
            <div className="voice-mode-copy">
              <strong>{voiceStatusCopy(voiceStatus)}</strong>
              <p>{voiceTranscript || "Fale naturalmente. A mensagem será enviada automaticamente."}</p>
            </div>
            <button
              className={`voice-main-action ${listening ? "listening" : ""}`}
              type="button"
              onClick={startVoiceCapture}
              disabled={listening || voiceStatus === "thinking" || voiceStatus === "speaking"}
            >
              <Mic /> {listening ? "Ouvindo…" : "Falar agora"}
            </button>
          </main>
          <footer className="voice-mode-footer">
            <span><i /> {chatDeviceId ? "Conectado ao computador selecionado" : "Respondido pelo servidor"}</span>
            <button type="button" onClick={closeVoiceMode}>Encerrar conversa</button>
          </footer>
        </div>
      )}
    </div>
  );
}

function MemoryPortal({ session }) {
  const [memories, setMemories] = useState([]);
  const [profile, setProfile] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [branches, setBranches] = useState([]);
  const [entities, setEntities] = useState([]);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [content, setContent] = useState("");
  const [memoryType, setMemoryType] = useState("profile");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  if (!session) return <Navigate to="/login" replace />;

  const headers = (json = false) => ({
    Authorization: `Bearer ${accessToken}`,
    ...(json ? { "Content-Type": "application/json" } : {}),
  });

  async function api(path, options = {}) {
    const response = await fetch(`${config.apiBaseUrl}${path}`, {
      ...options,
      headers: { ...headers(Boolean(options.body)), ...(options.headers || {}) },
    });
    if (response.status === 204) return null;
    const result = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(result.detail || "A memória não respondeu.");
    return result;
  }

  async function load() {
    setBusy(true);
    setNotice("");
    try {
      const [memoryData, profileData, snapshotData, branchData, entityData] = await Promise.all([
        api("/v1/memories?limit=200"),
        api("/v1/memory/profile"),
        api("/v1/memory/snapshots"),
        api("/v1/memory/branches"),
        api("/v1/memory/entities"),
      ]);
      setMemories(Array.isArray(memoryData?.items) ? memoryData.items : []);
      setProfile(profileData || {});
      setSnapshots(Array.isArray(snapshotData?.snapshots) ? snapshotData.snapshots : []);
      setBranches(Array.isArray(branchData?.branches) ? branchData.branches : []);
      setEntities(Array.isArray(entityData?.entities) ? entityData.entities : []);
    } catch (error) {
      setNotice(error.message);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    if (accessToken) void load();
  }, [accessToken]);

  async function addMemory(event) {
    event.preventDefault();
    if (!content.trim()) return;
    setBusy(true);
    try {
      await api("/v1/memories", {
        method: "POST",
        body: JSON.stringify({
          content: content.trim(), memory_type: memoryType, source: "web:explicit", confidence: 1,
        }),
      });
      setContent("");
      setNotice("Memória confirmada e versionada.");
      await load();
    } catch (error) { setNotice(error.message); } finally { setBusy(false); }
  }

  async function feedback(memoryId, signal) {
    try {
      await api(`/v1/memories/${encodeURIComponent(memoryId)}/feedback`, {
        method: "POST", body: JSON.stringify({ signal, context: "Portal /memoria" }),
      });
      setNotice(signal === "useful" ? "Memória reforçada." : "Feedback registrado para revisão.");
    } catch (error) { setNotice(error.message); }
  }

  async function remove(memory) {
    if (!window.confirm(`Esquecer esta memória?\n\n${memory.content}`)) return;
    try {
      await api(`/v1/memories/${encodeURIComponent(memory.memory_id)}`, { method: "DELETE" });
      setNotice("Memória removida com histórico preservado pelo snapshot de segurança.");
      await load();
    } catch (error) { setNotice(error.message); }
  }

  async function maintenance(operation) {
    setBusy(true);
    try {
      const result = await api(`/v1/memory/maintenance/${operation}`, {
        method: "POST", body: JSON.stringify({ force: false, mode: "candidates" }),
      });
      setNotice(`${operation}: ${JSON.stringify(result)}`);
      await load();
    } catch (error) { setNotice(error.message); } finally { setBusy(false); }
  }

  async function createSnapshot() {
    const name = window.prompt("Nome do ponto de restauração:", `perfil-${new Date().toISOString().slice(0, 10)}`);
    if (!name) return;
    try {
      await api("/v1/memory/snapshots", {
        method: "POST", body: JSON.stringify({ name, description: "Criado pelo portal Jarvis" }),
      });
      setNotice("Snapshot criado.");
      await load();
    } catch (error) { setNotice(error.message); }
  }

  async function rollback(name) {
    if (!window.confirm(`Restaurar a memória para o snapshot “${name}”?`)) return;
    try {
      await api(`/v1/memory/snapshots/${encodeURIComponent(name)}/rollback`, { method: "POST" });
      setNotice("Rollback concluído.");
      await load();
    } catch (error) { setNotice(error.message); }
  }

  async function createBranch() {
    const name = window.prompt("Nome da ramificação experimental:", "perfil_experimento");
    if (!name) return;
    try {
      await api("/v1/memory/branches", { method: "POST", body: JSON.stringify({ name }) });
      setNotice("Branch criada sem alterar a memória principal.");
      await load();
    } catch (error) { setNotice(error.message); }
  }

  async function branchAction(name, operation) {
    if (operation === "merge" && !window.confirm(`Mesclar a branch “${name}” na memória atual?`)) return;
    try {
      await api(`/v1/memory/branches/${encodeURIComponent(name)}/${operation}`, {
        method: "POST",
        ...(operation === "merge" ? { body: JSON.stringify({ strategy: "safe" }) } : {}),
      });
      setNotice(operation === "merge" ? "Branch mesclada com segurança." : `Branch “${name}” ativada.`);
      await load();
    } catch (error) { setNotice(error.message); }
  }

  const visible = memories.filter((memory) => {
    const matchesType = filter === "all" || memory.memory_type === filter;
    const matchesQuery = !query.trim() || String(memory.content || "").toLocaleLowerCase("pt-BR").includes(query.toLocaleLowerCase("pt-BR"));
    return matchesType && matchesQuery;
  });

  if (!session) return <Navigate to="/login" replace />;

  return (
    <div className="memory-portal">
      <header className="memory-topbar">
        <Brand compact />
        <nav><Link to="/app">Painel</Link><button onClick={() => supabase?.auth.signOut()}><LogOut /> Sair</button></nav>
      </header>
      <main>
        <section className="memory-hero">
          <div><span>MATRIXONE · MEMORIA</span><h1>Sua memória, sob seu controle.</h1><p>Perfil, hábitos, fatos e experiências com origem, confiança e histórico reversível.</p></div>
          <div className="memory-health"><i /><strong>{busy ? "SINCRONIZANDO" : "MEMÓRIA ONLINE"}</strong><small>{memories.length} registros ativos</small></div>
        </section>
        {notice && <div className="memory-notice">{notice}</div>}
        <section className="memory-stats">
          <article><Brain /><div><b>{profile?.stats?.total ?? memories.length}</b><span>memórias</span></div></article>
          <article><UserRound /><div><b>{memories.filter((m) => m.memory_type === "profile").length}</b><span>perfil</span></div></article>
          <article><GitBranch /><div><b>{branches.length}</b><span>branches</span></div></article>
          <article><Activity /><div><b>{entities.length}</b><span>entidades</span></div></article>
        </section>
        <section className="memory-layout">
          <div className="memory-main">
            <div className="memory-panel-heading"><div><span>PERFIL DERIVADO</span><h2>O que o Jarvis sabe sobre você</h2></div><button onClick={load}><RefreshCw /> Atualizar</button></div>
            <pre className="profile-summary">{profile?.profile || "O perfil será formado conforme você confirma fatos e preferências."}</pre>
            <form className="memory-add" onSubmit={addMemory}>
              <textarea value={content} onChange={(event) => setContent(event.target.value)} placeholder="Confirme algo que o Jarvis deve lembrar…" />
              <select value={memoryType} onChange={(event) => setMemoryType(event.target.value)}>
                <option value="profile">Perfil ou preferência</option><option value="semantic">Fato</option><option value="procedural">Hábito ou procedimento</option><option value="working">Contexto temporário</option><option value="episodic">Experiência</option>
              </select>
              <button className="button button--primary" disabled={busy}><Plus /> Memorizar</button>
            </form>
            <div className="memory-tools"><label><Search /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Pesquisar memória" /></label><select value={filter} onChange={(event) => setFilter(event.target.value)}><option value="all">Todos os tipos</option><option value="profile">Perfil</option><option value="semantic">Fatos</option><option value="procedural">Hábitos</option><option value="working">Temporárias</option><option value="episodic">Experiências</option></select></div>
            <div className="memory-list">
              {visible.map((memory) => <article className="memory-card" key={memory.memory_id}><div className="memory-card-meta"><span>{memory.memory_type || "semantic"}</span><small>{memory.created_at ? new Date(memory.created_at).toLocaleString("pt-BR") : ""}</small></div><p>{memory.content}</p><div className="memory-card-actions"><button title="Útil" onClick={() => feedback(memory.memory_id, "useful")}><ThumbsUp /></button><button title="Incorreta" onClick={() => feedback(memory.memory_id, "wrong")}><ThumbsDown /></button><button title="Apagar" onClick={() => remove(memory)}><Trash2 /></button></div></article>)}
              {!visible.length && <div className="empty-card">Nenhuma memória encontrada.</div>}
            </div>
          </div>
          <aside className="memory-side">
            <section><span>SAÚDE DA MEMÓRIA</span><h3>Governança</h3><button onClick={() => maintenance("governance")}><ShieldCheck /> Verificar qualidade</button><button onClick={() => maintenance("consolidate")}><AlertTriangle /> Detectar conflitos</button><button onClick={() => maintenance("reflect")}><Sparkles /> Descobrir padrões</button></section>
            <section><span>LINHA DO TEMPO</span><h3>Snapshots</h3><button onClick={createSnapshot}><Plus /> Criar ponto seguro</button>{snapshots.slice(0, 8).map((snapshot) => <div className="timeline-item" key={snapshot.name}><i /><div><strong>{snapshot.name}</strong><small>{snapshot.created_at || snapshot.timestamp || ""}</small></div><button title="Restaurar" onClick={() => rollback(snapshot.name)}><RotateCcw /></button></div>)}</section>
            <section><span>EXPERIMENTOS</span><h3>Branches</h3><button onClick={createBranch}><GitBranch /> Nova branch</button>{branches.map((branch) => <div className={`branch-item ${branch.active ? "active" : ""}`} key={branch.name}><div><strong>{branch.name}</strong><small>{branch.active ? "ATIVA" : "isolada"}</small></div>{!branch.active && <div className="branch-actions"><button title="Ativar branch" onClick={() => branchAction(branch.name, "checkout")}>Abrir</button><button title="Mesclar branch" onClick={() => branchAction(branch.name, "merge")}>Mesclar</button></div>}</div>)}</section>
          </aside>
        </section>
      </main>
    </div>
  );
}

export default function App() {
  const [session, setSession] = useState(undefined);
  const [path, setPath] = useState(window.location.pathname);

  useEffect(() => {
    if (!supabase) {
      setSession(null);
      return undefined;
    }
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data } = supabase.auth.onAuthStateChange((_event, next) => setSession(next));
    return () => data.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const updatePath = () => setPath(window.location.pathname);
    window.addEventListener("popstate", updatePath);
    return () => window.removeEventListener("popstate", updatePath);
  }, []);

  if (session === undefined) {
    return <div className="boot-screen"><img src="/jarvis-icon.png" alt="" /><span>INICIANDO J.A.R.V.I.S.</span></div>;
  }

  if (path === "/") return <LandingPage />;
  if (path === "/login") return <LoginPage session={session} />;
  if (path === "/memoria" || path.startsWith("/memoria/")) {
    return <MemoryPortal session={session} />;
  }
  if (path === "/app" || path.startsWith("/app/")) {
    return <AppShell session={session} />;
  }
  return <Navigate to="/" />;
}
